// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Colour means the same thing in every view. An ANZSIC division is the same
// hue in the scatter, the leaderboard, the treemap and the drawer, so a reader
// who learns "construction is the orange one" once never has to relearn it.
//
// Deaths are deliberately NOT rendered in an alarming red. People died; this
// is an annual statistical release, not an alert.

/** The 19 ANZSIC divisions. Ordered so adjacent divisions are distinguishable. */
export const DIVISION_COLORS: Record<string, string> = {
  A: '#6b8f3f', // agriculture, forestry and fishing
  B: '#8a6d3b', // mining
  C: '#b5651d', // manufacturing
  D: '#4d7f8c', // electricity, gas, water and waste
  E: '#c05621', // construction
  F: '#7e6aa8', // wholesale trade
  G: '#9b5a8f', // retail trade
  H: '#c2894b', // accommodation and food
  I: '#2f6f8f', // transport, postal and warehousing
  J: '#5b7fa6', // information media and telecommunications
  K: '#4a7a63', // financial and insurance
  L: '#8f7a4a', // rental, hiring and real estate
  M: '#5f7d9a', // professional, scientific and technical
  N: '#7a7f8c', // administrative and support
  O: '#3f6079', // public administration and safety
  P: '#4f8a7a', // education and training
  Q: '#a4544f', // health care and social assistance
  R: '#8a6aa0', // arts and recreation
  S: '#6e7a86', // other services
};

export const DIVISION_FALLBACK = '#8b96a0';

export function divisionColor(code: string): string {
  const div = code.replace(/[^A-Z]/g, '').charAt(0);
  return DIVISION_COLORS[div] ?? DIVISION_FALLBACK;
}

/** Physical injury vs psychological injury — used wherever the two are compared. */
export const PHYSICAL = '#3f6079';
export const PSYCH = '#b1543f';

/** Deaths. A muted slate, never a warning red. */
export const FATAL = '#41576b';
export const FATAL_LIGHT = '#93a6b6';

/** Money and time-off encodings. */
export const COST = '#8a5a2b';
export const TIME = '#4a7a7f';

/** Enforcement activity. */
export const ENFORCE = '#5a6f4a';

/** Hazard-exposure ramp: low exposure -> high exposure. Sequential, not diverging. */
const HAZARD_RAMP = ['#f4f2ee', '#dfe4e0', '#c2cfc6', '#9fb6a8', '#7d9c8b', '#5c8270', '#3d6a58'];

export function hazardColor(score: number | null): string {
  if (score === null || !Number.isFinite(score)) return '#ffffff';
  const t = Math.max(0, Math.min(1, score / 100));
  const i = Math.min(HAZARD_RAMP.length - 1, Math.floor(t * HAZARD_RAMP.length));
  return HAZARD_RAMP[i];
}

export const HAZARD_STOPS = HAZARD_RAMP;

/** Intensity ramp for count heatmaps, on the same warm-neutral family. */
const COUNT_RAMP = ['#f6f3ef', '#eadfd2', '#dcc6ae', '#cbab8b', '#b98f6b', '#a3744e', '#875a37'];

export function countColor(t: number): string {
  const x = Math.max(0, Math.min(1, t));
  const i = Math.min(COUNT_RAMP.length - 1, Math.floor(x * COUNT_RAMP.length));
  return COUNT_RAMP[i];
}

export const COUNT_STOPS = COUNT_RAMP;

/** Jurisdiction colours, stable across the enforcement small multiples. */
export const JURISDICTIONS = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'] as const;

export const JURISDICTION_NAMES: Record<string, string> = {
  NSW: 'New South Wales', VIC: 'Victoria', QLD: 'Queensland', SA: 'South Australia',
  WA: 'Western Australia', TAS: 'Tasmania', NT: 'Northern Territory',
  ACT: 'Australian Capital Territory', ACTPrivate: 'ACT (private sector)', ACTGov: 'ACT (government)',
  Comcare: 'Comcare', Seacare: 'Seacare', 'Aus Gov': 'Australian Government', AUST: 'Australia',
};
